import type { ReactNode, SVGProps } from "react";
import { cx } from "../cx";

/**
 * Icon-mirroring convention (see README "Icons in RTL"):
 * - MIRROR icons whose meaning is a direction along the reading axis: back/forward
 *   chevrons and arrows, "send", reply, undo/redo, list indent, text-align start/end,
 *   progress/steppers, a sidebar-toggle. Name them logically (`ChevronForward`, not
 *   `ChevronRight`) and set `mirrorInRtl`.
 * - NEVER mirror: media controls (play/pause/fast-forward follow tape direction),
 *   clocks/refresh (clockwise everywhere), checkmarks, close ×, search magnifier,
 *   brand logos, charts' time axis, phone/handset, and anything containing Latin text
 *   or digits.
 *
 * Mirroring is done with the `rtl:` variant (`rtl:-scale-x-100`), so it reacts to the
 * live `<html dir>` without re-rendering.
 */
export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "children"> {
  /** Accessible name; omit for decorative icons (then `aria-hidden`). */
  readonly label?: string;
  readonly mirrorInRtl?: boolean;
}

function Icon({
  label,
  mirrorInRtl = false,
  className,
  children,
  ...rest
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
      className={cx("size-5 shrink-0", mirrorInRtl && "rtl:-scale-x-100", className)}
      {...rest}
    >
      {children}
    </svg>
  );
}

// Directional (drawn for LTR, mirrored in RTL).
export const ChevronForwardIcon = (p: IconProps) => (
  <Icon mirrorInRtl {...p}>
    <path d="m9 6 6 6-6 6" />
  </Icon>
);
export const ChevronBackIcon = (p: IconProps) => (
  <Icon mirrorInRtl {...p}>
    <path d="m15 6-6 6 6 6" />
  </Icon>
);
export const ArrowForwardIcon = (p: IconProps) => (
  <Icon mirrorInRtl {...p}>
    <path d="M4 12h15m-5-6 6 6-6 6" />
  </Icon>
);
export const ArrowBackIcon = (p: IconProps) => (
  <Icon mirrorInRtl {...p}>
    <path d="M20 12H5m5-6-6 6 6 6" />
  </Icon>
);
export const SendIcon = (p: IconProps) => (
  <Icon mirrorInRtl {...p}>
    <path d="M4 12 20 4l-4 16-4-7-8-1Z" />
  </Icon>
);

// Non-directional (never mirrored).
export const CloseIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);
export const CheckCircleIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8 12.5 2.5 2.5L16 9.5" />
  </Icon>
);
export const AlertCircleIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5.5M12 16.5v.01" />
  </Icon>
);
export const InfoCircleIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5M12 7.5v.01" />
  </Icon>
);
export const SearchIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.2-4.2" />
  </Icon>
);
export const EyeIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="2.75" />
  </Icon>
);
export const EyeOffIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 4l16 16M10.6 6A9.8 9.8 0 0 1 12 5.5C18 5.5 21.5 12 21.5 12a17 17 0 0 1-2.9 3.7M6.6 7.2A16.8 16.8 0 0 0 2.5 12S6 18.5 12 18.5a9.4 9.4 0 0 0 4.2-1" />
    <path d="M9.9 10a2.75 2.75 0 0 0 4 3.9" />
  </Icon>
);
export const PlusIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);
export const PlayIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7 5v14l12-7L7 5Z" />
  </Icon>
);
export const SpinnerIcon = ({ className, ...p }: IconProps) => (
  <Icon className={cx("animate-spin motion-reduce:animate-none", className)} {...p}>
    <path d="M12 3a9 9 0 1 0 9 9" />
  </Icon>
);
// Added in WP-111 (onboarding wizard, workspace switcher, settings). None mirror.
export const LockIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
    <path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3" />
  </Icon>
);
export const CheckIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Icon>
);
export const ChevronDownIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
);
// OWN-163 (owner assistant). Not mirrored.
export const SparklesIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 4.5 11.8 9.2 16.5 11 11.8 12.8 10 17.5 8.2 12.8 3.5 11 8.2 9.2 10 4.5Z" />
    <path d="M18 3.5v4M16 5.5h4M18 15v4M16 17h4" />
  </Icon>
);
